package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.WebImportRouteStrings
import com.milta.core.localization.deliveriesSummaryText
import com.milta.core.localization.filterAllText
import com.milta.core.localization.filterToFixText
import com.milta.core.localization.fixAddressesText
import com.milta.core.localization.reasonText
import com.milta.core.localization.toFixCountText
import com.milta.feature.delivery.domain.entity.AddressSuggestion
import com.milta.feature.delivery.domain.usecase.ValidateAddressUseCase
import com.milta.feature.delivery.domain.repository.DeliveryRepository
import com.milta.feature.optimizationquota.domain.entity.OptimizationQuota
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.feature.route.domain.entity.GeocodeFailureReason
import com.milta.feature.route.domain.entity.GeocodeStatus
import com.milta.feature.route.domain.entity.foundAddressLabel
import com.milta.feature.route.domain.entity.LoadingAddress
import com.milta.feature.route.presentation.DeliveryFilter
import com.milta.feature.route.presentation.WebImportRouteIntent
import com.milta.feature.route.presentation.WebImportRouteRow
import com.milta.feature.route.presentation.WebImportRouteState
import com.milta.feature.team.domain.entity.Driver
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.koin.compose.koinInject

/**
 * Écran 2 (+2b/2c/2d) — file-card, driver assignment card, submit CTA (left column) and the stops
 * table with review/correction (right column), mockup `docs/mockups/MIL-50.html`'s `.verify-grid`.
 */
@Composable
internal fun WebVerifyAssignRouteScreen(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    /** MIL-97 — the Enterprise's own optimization counter; `null` until read (no counter shown). */
    quota: OptimizationQuota? = null,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    // Retour 26/10 (R9) — "Ajouter des livraisons" menu, purely a display toggle.
    var showAddMenu by remember { mutableStateOf(false) }
    // Confirmation locale à l'écran — pas dans le ViewModel/state partagé, même précédent que
    // editingRowId ci-dessus : purement un toggle d'affichage, l'intent réel (DiscardDraft) n'est
    // émis qu'à la confirmation.
    var showDiscardConfirm by remember { mutableStateOf(false) }

    Div {
        AddedFileBanner(
            analysingFileName = state.addedFileName,
            failure = state.addedFileFailure,
            onDismissFailure = { onIntent(WebImportRouteIntent.DismissAddedFileFailure) },
        )
        // Écran 2d — bandeau "tournée déjà envoyée" (409), au-dessus de la grille.
        if (state.submitError == "already_dispatched") {
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = strings.alreadyDispatchedBanner)
            }
        }

        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "270px 1fr")
                    property("gap", "18px")
                    property("align-items", "start")
                }
            },
        ) {
            Div {
                FileCard(state, strings, onIntent, onDiscardClick = { showDiscardConfirm = true })
                MultiDriverAssignCard(
                    state = state,
                    strings = strings,
                    onToggleDriver = { driverId -> onIntent(WebImportRouteIntent.ToggleDriver(driverId)) },
                )
                LoadingAddressCard(state, onIntent)
                SubmitButton(state, strings, quota, onIntent)
            }

            TableCard(
                state = state,
                strings = strings,
                onAddDeliveries = { showAddMenu = true },
                onIntent = onIntent,
            )
        }

        if (showDiscardConfirm) {
            DiscardDraftDialog(
                strings = strings,
                onCancel = { showDiscardConfirm = false },
                onConfirm = {
                    showDiscardConfirm = false
                    onIntent(WebImportRouteIntent.DiscardDraft)
                },
            )
        }

        if (showAddMenu) {
            AddDeliveriesMenuDialog(rowCount = state.rows.size, onIntent = onIntent, onDismiss = { showAddMenu = false })
        }
        state.rowEditor?.let { editor ->
            RowEditorDialog(
                editor = editor,
                initial = editor.rowId?.let { id -> state.rows.firstOrNull { it.id == id } },
                onIntent = onIntent,
            )
        }

        state.loadingAddressPendingDeletion?.let { address ->
            DeleteLoadingAddressDialog(
                address = address,
                onCancel = { onIntent(WebImportRouteIntent.CancelDeleteLoadingAddress) },
                onConfirm = { onIntent(WebImportRouteIntent.ConfirmDeleteLoadingAddress) },
            )
        }
    }
}

/** MIL-99 — "Adresse de chargement" card, same card/eyebrow style as [MultiDriverAssignCard]: the
 * saved addresses inline (selected row with the active tint), a HERE-backed "Nouvelle adresse"
 * field, per-row delete behind [DeleteLoadingAddressDialog]. */
@Composable
private fun LoadingAddressCard(state: WebImportRouteState, onIntent: (WebImportRouteIntent) -> Unit) {
    val strings = LocalStrings.current.loadingAddress
    val commonStrings = LocalStrings.current.common
    var showNewAddressField by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    val isMissing = state.selectedLoadingAddressId == null && !state.isLoadingLoadingAddresses
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid " + (if (isMissing) WebTealAccent else WebBorder))
                property("border-radius", "12px")
                padding(16.px)
                property("margin-bottom", "16px")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.06em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); property("margin-bottom", "10px") } },
        ) { Text(strings.title) }

        if (isMissing) {
            InlineBanner(kind = InlineBannerKind.Info, text = strings.emptyMessage)
        }

        if (state.isLoadingLoadingAddresses) {
            Div(attrs = { style { padding(10.px, 0.px); property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text("…") }
        } else {
            Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "6px") } }) {
                state.loadingAddresses.forEach { address ->
                    LoadingAddressRow(
                        address = address,
                        selected = address.id == state.selectedLoadingAddressId,
                        startPointBadge = strings.startPointBadge,
                        deleteLabel = commonStrings.delete,
                        onClick = { onIntent(WebImportRouteIntent.SelectLoadingAddress(address.id)) },
                        onDelete = { onIntent(WebImportRouteIntent.RequestDeleteLoadingAddress(address)) },
                    )
                }
            }
        }

        Div(attrs = { style { property("margin-top", "10px") } }) {
            if (showNewAddressField || (state.loadingAddresses.isEmpty() && !state.isLoadingLoadingAddresses)) {
                AddressAutocompleteInput(
                    value = query,
                    placeholderText = strings.webSearchPlaceholder,
                    enabled = !state.isSavingLoadingAddress,
                    onChange = { query = it },
                    onSuggestionPicked = { suggestion ->
                        query = ""
                        showNewAddressField = false
                        onIntent(WebImportRouteIntent.AddLoadingAddress(suggestion))
                    },
                )
            } else {
                Div(
                    attrs = {
                        onClick { showNewAddressField = true }
                        style { secondaryButtonStyle(this) }
                    },
                ) {
                    Span(attrs = { style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "6px") } }) {
                        WebIcon(WebIconName.Add, size = 16)
                        Text(strings.newAddressAction)
                    }
                }
            }
        }

        if (state.isSavingLoadingAddress) {
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "6px") } }) {
                Text(commonStrings.loading)
            }
        }

        state.loadingAddressError?.let { error ->
            Div(attrs = { style { property("font-size", "12px"); property("color", WebErrorInk); property("margin-top", "6px") } }) {
                Text(if (error == "offline") commonStrings.errorOffline else LocalStrings.current.webImportRoute.submitServerError)
            }
        }
    }
}

@Composable
private fun LoadingAddressRow(
    address: LoadingAddress,
    selected: Boolean,
    startPointBadge: String,
    deleteLabel: String,
    onClick: () -> Unit,
    onDelete: () -> Unit,
) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                padding(9.px, 10.px)
                property("border", "1px solid " + (if (selected) WebTealAccent else WebBorder))
                property("border-radius", "9px")
                property("background", if (selected) WebActiveTint else WebSurface)
                property("cursor", "pointer")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    width(20.px); height(20.px)
                    property("border-radius", "50%"); property("flex-shrink", "0")
                    property("box-sizing", "border-box")
                    property("border", "2px solid " + (if (selected) WebTealAccent else WebBorder))
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                }
            },
        ) {
            if (selected) {
                Div(attrs = { style { width(10.px); height(10.px); property("border-radius", "50%"); property("background", WebTealAccent) } })
            }
        }
        Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
            Div(attrs = { style { property("font-size", "13px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(address.title) }
            if (address.subtitle.isNotBlank()) {
                Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(address.subtitle) }
            }
            if (selected) {
                Div(
                    attrs = {
                        style {
                            property("display", "inline-block"); property("margin-top", "3px")
                            property("font-size", "10.5px"); property("font-weight", "700")
                            property("color", WebInProgressInk); property("background", WebAccentLight)
                            property("border-radius", "999px"); padding(2.px, 8.px)
                        }
                    },
                ) { Text(startPointBadge) }
            }
        }
        Button(
            attrs = {
                attr("aria-label", deleteLabel)
                attr("title", deleteLabel)
                onClick { event ->
                    event.stopPropagation()
                    onDelete()
                }
                style {
                    width(28.px); height(28.px); property("border-radius", "8px"); property("flex-shrink", "0")
                    property("border", "1px solid $WebBorder"); property("background", WebSurface)
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("cursor", "pointer"); property("color", WebTextSecondary)
                }
            },
        ) { WebIcon(WebIconName.Delete, size = 16) }
    }
}

/** MIL-99 — same look as [DiscardDraftDialog]. */
@Composable
private fun DeleteLoadingAddressDialog(address: LoadingAddress, onCancel: () -> Unit, onConfirm: () -> Unit) {
    val strings = LocalStrings.current.loadingAddress
    val commonStrings = LocalStrings.current.common
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface)
                    property("border-radius", "14px")
                    width(360.px)
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
                Text(strings.deleteDialogTitle)
            }
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextSecondary); property("line-height", "1.5") } }) {
                Text(strings.deleteDialogMessage.replace("%s", address.title))
            }
            Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "20px") } }) {
                Button(
                    attrs = {
                        onClick { onCancel() }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebTextPrimary)
                            property("font-size", "13px"); property("font-weight", "600")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.cancel) }
                Button(
                    attrs = {
                        onClick { onConfirm() }
                        style {
                            property("border", "none"); property("border-radius", "8px")
                            property("background", WebError); property("color", "#FFFFFF")
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.delete) }
            }
        }
    }
}

@Composable
private fun FileCard(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onIntent: (WebImportRouteIntent) -> Unit,
    onDiscardClick: () -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebSuccessInk")
                property("border-radius", "12px")
                padding(14.px)
                property("margin-bottom", "14px")
            }
        },
    ) {
        Div(
            attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebSuccessInk); property("margin-bottom", "10px") } },
        ) {
            Div(
                attrs = { style { width(18.px); height(18.px); property("border-radius", "50%"); property("background", WebSuccessInk); property("color", "#FFFFFF"); property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("font-size", "11px") } },
            ) { Text("✓") }
            Text(strings.fileImportedStatus)
        }
        Div(attrs = { style { property("display", "flex"); property("gap", "10px"); property("align-items", "center") } }) {
            Div(
                attrs = { style { width(36.px); height(36.px); property("border-radius", "8px"); property("background", WebSurfaceDim); property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("flex-shrink", "0") } },
            ) { WebIcon(WebIconName.Description, size = 20, color = WebTextSecondary) }
            Div {
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(state.fileName) }
                Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "1px") } }) {
                    Text("${state.totalCount} ${strings.stopsDetectedSuffix}")
                }
            }
        }
        // Retour client — les deux actions secondaires d'Écran 2 partagent désormais le même style
        // de bouton (secondaryButtonStyle), seule la couleur distingue "Annuler" (destructif) de
        // "Remplacer" (neutre) — avant, l'un était un simple lien texte et l'autre un style
        // différent, à côté du vrai bouton CTA "Optimiser l'itinéraire", peu lisible.
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("margin-top", "12px") } }) {
            Div(
                attrs = {
                    onClick { onDiscardClick() }
                    style { secondaryButtonStyle(this, danger = true) }
                },
            ) { Text(strings.discardDraftButton) }
        }
    }
}

@Composable
private fun DiscardDraftDialog(strings: WebImportRouteStrings, onCancel: () -> Unit, onConfirm: () -> Unit) {
    val commonStrings = LocalStrings.current.common
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface)
                    property("border-radius", "14px")
                    width(360.px)
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
                Text(strings.discardDraftDialogTitle)
            }
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextSecondary); property("line-height", "1.5") } }) {
                Text(strings.discardDraftDialogMessage)
            }
            Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "20px") } }) {
                Button(
                    attrs = {
                        onClick { onCancel() }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebTextPrimary)
                            property("font-size", "13px"); property("font-weight", "600")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.cancel) }
                Button(
                    attrs = {
                        onClick { onConfirm() }
                        style {
                            property("border", "none"); property("border-radius", "8px")
                            property("background", WebError); property("color", "#FFFFFF")
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(strings.discardDraftConfirmButton) }
            }
        }
    }
}

/** MIL-51 — replaces the former single-selection dropdown ([AssignCard]) with a checkbox list: any
 * number of the enterprise's drivers can now be checked, always submitted through `POST
 * /route-batches` (see [WebImportRouteViewModel.submit]). */
@Composable
private fun MultiDriverAssignCard(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onToggleDriver: (String) -> Unit,
) {
    val isInvalid = state.submitError == "driver_required"
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid " + (if (isInvalid) WebError else WebBorder))
                property("border-radius", "12px")
                padding(16.px)
                property("margin-bottom", "16px")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.06em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); property("margin-bottom", "10px") } },
        ) { Text(strings.assignToEyebrow) }

        when {
            state.isLoadingDrivers -> Div(attrs = { style { padding(10.px, 0.px); property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text("…") }

            state.drivers.isEmpty() -> Div(attrs = { style { padding(10.px, 0.px); property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(strings.dashPlaceholder) }

            else -> {
                Div(
                    attrs = { style { property("border", "1px solid $WebBorder"); property("border-radius", "9px"); property("overflow", "hidden") } },
                ) {
                    state.drivers.forEachIndexed { index, driver ->
                        DriverCheckboxRow(
                            driver = driver,
                            selected = driver.id in state.selectedDriverIds,
                            showBottomBorder = index < state.drivers.lastIndex,
                            onClick = { onToggleDriver(driver.id) },
                        )
                    }
                }
                Div(
                    attrs = { style { property("font-size", "12px"); property("font-weight", "700"); property("color", WebPrimary); property("margin-top", "8px"); property("text-align", "right") } },
                ) { Text("${state.selectedDriverIds.size} ${strings.selectedDriversCountSuffix}") }
            }
        }

        if (isInvalid) {
            Div(attrs = { style { property("font-size", "12px"); property("color", WebErrorInk); property("margin-top", "6px") } }) {
                Text(strings.selectDriverRequiredError)
            }
        }
    }
}

@Composable
private fun DriverCheckboxRow(driver: Driver, selected: Boolean, showBottomBorder: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                padding(10.px, 14.px)
                property("font-size", "13.5px")
                property("background", WebSurface)
                property("cursor", "pointer")
                if (showBottomBorder) property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column") } }) {
            Div(attrs = { style { property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text("${driver.firstName} ${driver.lastName}") }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(driver.phoneNumber) }
        }
        Div(
            attrs = {
                style {
                    width(20.px); height(20.px)
                    property("border-radius", "6px")
                    property("flex-shrink", "0")
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("background", if (selected) WebPrimary else WebSurfaceDim)
                    property("border", "1px solid " + (if (selected) WebPrimary else WebBorder))
                    property("color", "#FFFFFF"); property("font-size", "12px"); property("font-weight", "700")
                }
            },
        ) { if (selected) WebIcon(WebIconName.Check, size = 14, color = "#FFFFFF") }
    }
}

/** MIL-91 — a row is ready to be sent only with a non-blank address HERE located (Geocoded) AND its GPS. */
private fun WebImportRouteRow.isLocated(): Boolean =
    !address.isNullOrBlank() && geocodeStatus == GeocodeStatus.Geocoded && latitude != null && longitude != null

@Composable
private fun SubmitButton(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    quota: OptimizationQuota?,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    val quotaStrings = LocalStrings.current.optimizationQuota
    // MIL-97 — E1: counter on "Optimiser l'itinéraire" only (RoutaR enabled). At 0 the button is
    // disabled in advance (R10) and "Envoyer sans optimisation" keeps the entered order (R6).
    val shownQuota = quota.takeIf { state.routarEnabled }
    val quotaExhausted = shownQuota?.isExhausted == true
    if (shownQuota != null && quotaExhausted) {
        QuotaWarnBanner(
            title = quotaStrings.exhaustedTitle,
            message = quotaStrings.exhaustedCreateBatchMessage.fillPlaceholders(
                "limit" to shownQuota.globalLimit,
                "date" to OptimizationQuotaFormat.dayMonth(shownQuota.resetAt),
            ),
        )
    }
    // Retour 26/10 (R2) — an address to correct (refused by HERE, or missing) blocks both buttons;
    // HERE unreachable does not (R3). No "Continuer quand même" warning any more.
    val toFix = state.toFixCount
    val blankAddress = toFix > 0
    fun submitOrWarn(intent: WebImportRouteIntent) = onIntent(intent)
    // MIL-99 — blocked until a loading address is selected.
    val noLoadingAddress = state.selectedLoadingAddressId == null
    val driverCount = state.selectedDriverIds.size
    val baseDisabled = state.isSubmitting || blankAddress || noLoadingAddress || driverCount == 0 ||
        state.submitError == "already_dispatched"
    val disabled = baseDisabled || quotaExhausted
    Button(
        attrs = {
            if (!disabled) onClick { submitOrWarn(WebImportRouteIntent.Submit) }
            style {
                gradientButtonStyle(this)
                width(100.percent)
                if (disabled) {
                    property("opacity", "0.5")
                    property("cursor", "not-allowed")
                    property("box-shadow", "none")
                }
            }
        },
    ) {
        if (state.isSubmitting) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("gap", "10px") } }) {
                WebSpinner(size = 16, strokeWidth = 2, color = "#FFFFFF", trackColor = "rgba(255,255,255,0.35)")
                Text(LocalStrings.current.common.creationInProgressDoNotClose)
            }
        } else {
            // Ad hoc feature (2026-08) — RoutaR kill switch: when known off ahead of time, this is
            // the only submit CTA shown at all (never "Optimiser l'itinéraire", which would fail
            // every time) — same single button, relabeled, dispatching with skipOptimization = true.
            // R5 — greyed, the text says what is missing.
            val routeStrings = LocalStrings.current.route
            Text(
                when {
                    toFix > 0 -> LocalStrings.current.routeCreation.fixAddressesText(toFix)
                    driverCount == 0 -> LocalStrings.current.routeCreation.chooseAtLeastOneDriver
                    !state.routarEnabled -> strings.sendWithoutOptimizationButton
                    driverCount > 1 -> "${routeStrings.routeBatchSubmitButtonPrefix} $driverCount ${routeStrings.routeBatchDriversWord}"
                    else -> strings.optimizeRouteButton
                },
            )
        }
    }
    if (quotaExhausted) {
        Button(
            attrs = {
                if (!baseDisabled) onClick { submitOrWarn(WebImportRouteIntent.SubmitWithoutOptimization) }
                style {
                    secondaryButtonStyle(this)
                    width(100.percent)
                    property("margin-top", "8px")
                    if (baseDisabled) {
                        property("opacity", "0.5")
                        property("cursor", "not-allowed")
                    }
                }
            },
        ) { Text(strings.sendWithoutOptimizationButton) }
    }

    // MIL-99 — `locationBlockedHint` ("chaque ligne doit avoir une adresse localisée…") is no longer
    // shown: sending isn't blocked by an unlocated row anymore, and a blank address row is already
    // flagged in the table itself (rowFlagMissingAddress + the incomplete-stops banner).

    if (noLoadingAddress) {
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "8px") } }) {
            Text(LocalStrings.current.loadingAddress.webSubmitBlockedHint)
        }
    }

    if (state.submitError != null && state.submitError != "driver_required" && state.submitError != "already_dispatched") {
        val message = when (state.submitError) {
            "loading_address_required" -> LocalStrings.current.loadingAddress.errorRequired
            "addresses_to_fix" -> LocalStrings.current.routeCreation.errorAddressesToFix
            "addresses_to_fix_retry" -> LocalStrings.current.routeCreation.errorAddressesCheckRetry
            "route_not_found" -> strings.routeNotFoundError
            "validation_error" -> strings.validationErrorGeneric
            "optimization_unavailable" -> strings.submitOptimizationUnavailableError
            else -> strings.submitServerError
        }
        Div(attrs = { style { property("font-size", "12px"); property("color", WebErrorInk); property("margin-top", "8px") } }) { Text(message) }
    }

    // Écran 2d — RoutaR indisponible : offre le round-robin déjà utilisé côté MIL-39 (`optimize =
    // false`) plutôt que de bloquer le patron sur "réessayer plus tard".
    if (state.submitError == "optimization_unavailable") {
        Button(
            attrs = {
                if (!state.isSubmitting && !blankAddress && !noLoadingAddress) onClick { submitOrWarn(WebImportRouteIntent.SubmitWithoutOptimization) }
                style {
                    width(100.percent)
                    property("margin-top", "8px")
                    property("padding", "10px")
                    property("border-radius", "10px")
                    property("border", "1px solid $WebBorder")
                    property("background", WebSurface)
                    property("color", WebTextPrimary)
                    property("font-size", "13px"); property("font-weight", "600")
                    if (state.isSubmitting || blankAddress || noLoadingAddress) {
                        property("opacity", "0.5")
                        property("cursor", "not-allowed")
                    } else {
                        property("cursor", "pointer")
                    }
                }
            },
        ) { Text(strings.sendWithoutOptimizationButton) }
    }
}

@Composable
private fun TableCard(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    onAddDeliveries: () -> Unit,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    val routeCreation = LocalStrings.current.routeCreation
    val toFix = state.toFixCount
    Div(
        attrs = { style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); property("overflow", "hidden") } },
    ) {
        // Retour 26/10 — "N livraisons · N colis · N à corriger", filter, "Ajouter des livraisons".
        Div(
            attrs = { style { property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "12px"); property("align-items", "center"); padding(12.px, 16.px); property("border-bottom", "1px solid $WebBorder") } },
        ) {
            Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) {
                // Retour 28/10 — "N à corriger" only when there is any, nothing otherwise.
                Text(routeCreation.deliveriesSummaryText(state.rows.size, state.packagesCount))
                if (toFix > 0) {
                    Text(" · ")
                    Span(attrs = { style { property("color", WebErrorInk) } }) {
                        Text(routeCreation.toFixCountText(toFix))
                    }
                }
            }
            Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("margin-left", "auto") } }) {
                FilterChip(routeCreation.filterAllText(state.rows.size), selected = state.filter == DeliveryFilter.All, danger = false) {
                    onIntent(WebImportRouteIntent.SetFilter(DeliveryFilter.All))
                }
                FilterChip(routeCreation.filterToFixText(toFix), selected = state.filter == DeliveryFilter.ToFix, danger = true) {
                    onIntent(WebImportRouteIntent.SetFilter(DeliveryFilter.ToFix))
                }
            }
            Button(
                attrs = {
                    onClick { onAddDeliveries() }
                    style {
                        property("display", "flex"); property("align-items", "center"); property("gap", "6px")
                        property("border", "1px dashed $WebPrimaryMid"); property("background", WebSurface); property("color", WebPrimary)
                        property("border-radius", "9px"); padding(7.px, 12.px); property("font-weight", "700"); property("font-size", "12.5px")
                        property("cursor", "pointer")
                    }
                },
            ) {
                WebIcon(WebIconName.Add, size = 16)
                Text(LocalStrings.current.route.addDeliveriesAction)
            }
        }

        if (!state.isStructuredFormat || state.incompleteCount > 0) {
            Div(attrs = { style { padding(14.px, 18.px, 0.px, 18.px) } }) {
                if (!state.isStructuredFormat) {
                    InlineBanner(kind = InlineBannerKind.Info, text = strings.freeFormatBanner)
                    Div(attrs = { style { height(8.px) } })
                }
                if (state.incompleteCount > 0) {
                    InlineBanner(kind = InlineBannerKind.Warning, text = "${state.incompleteCount} ${strings.incompleteStopsBannerSuffix}")
                }
            }
        }

        Table(attrs = { style { width(100.percent); property("border-collapse", "collapse"); property("font-size", "13.5px") } }) {
            Thead {
                Tr {
                    HeaderCell(strings.columnHeaderIndex)
                    HeaderCell(strings.columnHeaderRecipientName)
                    HeaderCell(strings.columnHeaderAddress)
                    HeaderCell(strings.columnHeaderPhone)
                    HeaderCell(strings.columnHeaderDeadline)
                    HeaderCell(strings.columnHeaderPackageCount)
                    HeaderCell("")
                }
            }
            Tbody {
                val positions = state.rows.withIndex().associate { (index, row) -> row.id to index + 1 }
                state.visibleRows.forEach { row ->
                    RowView(
                        index = positions[row.id] ?: 0,
                        row = row,
                        strings = strings,
                        onEdit = { onIntent(WebImportRouteIntent.OpenRowEditor(row.id)) },
                        onRemove = { onIntent(WebImportRouteIntent.RemoveRow(row.id)) },
                        onRowChanged = { updated -> onIntent(WebImportRouteIntent.UpdateRow(row.id, updated)) },
                    )
                }
                if (state.visibleRows.isEmpty()) {
                    Tr {
                        Td(attrs = {
                            attr("colspan", "7")
                            style { padding(30.px); property("text-align", "center"); property("color", WebTextSecondary) }
                        }) { Text(routeCreation.allAddressesCorrect) }
                    }
                }
            }
        }
    }
}

/** MIL-91 — re-runs HERE validation of the row's address ([ValidateAddressUseCase]) and updates its status/GPS in place. */
@Composable
private fun RetryGeocodeLink(row: WebImportRouteRow, onRowChanged: (WebImportRouteRow) -> Unit) {
    val validateAddress: ValidateAddressUseCase = koinInject()
    val scope = rememberCoroutineScope()
    var retrying by remember { mutableStateOf(false) }
    Div(
        attrs = {
            if (!retrying) {
                onClick {
                    retrying = true
                    scope.launch {
                        try {
                            val validation = validateAddress(row.address.orEmpty().trim())
                            onRowChanged(row.copy(geocodeStatus = validation.geocodeStatus, geocodeFailureReason = validation.geocodeFailureReason, resolvedAddress = validation.resolvedAddress, latitude = validation.latitude, longitude = validation.longitude))
                        } catch (e: CancellationException) {
                            throw e
                        } catch (e: Exception) {
                            // Stays "to review": the manager can retry again or correct the row (✎).
                        } finally {
                            retrying = false
                        }
                    }
                }
            }
            style {
                property("font-size", "11.5px"); property("font-weight", "700"); property("color", WebPrimary)
                property("margin-top", "2px"); property("width", "fit-content")
                property("cursor", if (retrying) "wait" else "pointer")
            }
        },
    ) { Text(if (retrying) "…" else LocalStrings.current.route.importReviewRetryGeocodeAction) }
}

@Composable
private fun HeaderCell(label: String) {
    Th(
        attrs = { style { property("text-align", "left"); property("font-size", "10.5px"); property("font-weight", "700"); property("letter-spacing", "0.05em"); property("text-transform", "uppercase"); property("color", WebTextSecondary); padding(10.px, 16.px); property("background", WebSurfaceDim); property("border-bottom", "1px solid $WebBorder") } },
    ) { Text(label) }
}

@Composable
private fun RowView(
    index: Int,
    row: WebImportRouteRow,
    strings: WebImportRouteStrings,
    onEdit: () -> Unit,
    onRemove: () -> Unit,
    onRowChanged: (WebImportRouteRow) -> Unit,
) {
    // R2 — a row to correct reads pale red (an incomplete name alone keeps the warning tint).
    val fixReason = row.addressFixReason
    Tr(attrs = {
        when {
            row.needsAddressFix -> style { property("background", "#FFF8F8") }
            row.isIncomplete -> style { property("background", WebWarningBg) }
        }
    }) {
        Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextSecondary); property("border-bottom", "1px solid $WebSurfaceDim") } }) { Text(index.toString()) }

        // Retour 26/10 (R4) — editing goes through the modal (RowEditorDialog), checked at "Enregistrer".
        run {
            Td(attrs = { style { padding(12.px, 16.px); property("font-weight", "600"); property("color", WebTextPrimary); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.recipientName?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
                if (row.recipientName.isNullOrBlank()) {
                    Div(attrs = { style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "5px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebWarning); property("margin-top", "2px") } }) {
                        Text(strings.rowFlagMissingName)
                    }
                }
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", if (row.address.isNullOrBlank()) WebTextSecondary else WebTextPrimary); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.address?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
                // Retour 29/10 — "Trouvée : …" when HERE's match differs from the text typed.
                foundAddressLabel(row.geocodeStatus, row.address, row.resolvedAddress)?.let { found ->
                    Div(attrs = { style { property("font-size", "11.5px"); property("color", WebTextSecondary); property("margin-top", "2px") } }) {
                        Text(LocalStrings.current.common.addressFoundLine.replace("%1", found))
                    }
                }
                if (row.address.isNullOrBlank()) {
                    Div(attrs = { style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "5px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebWarning); property("margin-top", "2px") } }) {
                        Text(strings.rowFlagMissingAddress)
                    }
                } else if (fixReason != null) {
                    // R11 — "Numéro de rue manquant" / "Adresse introuvable".
                    Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "4px"); property("font-size", "11.5px"); property("font-weight", "700"); property("color", WebErrorInk); property("margin-top", "2px") } }) {
                        WebIcon(WebIconName.WarningAmber, size = 14, color = WebErrorInk)
                        Text(LocalStrings.current.routeCreation.reasonText(fixReason))
                    }
                    // An unverified address (HERE unreachable, call failed) keeps its retry link.
                    if (fixReason == GeocodeFailureReason.Unverified) RetryGeocodeLink(row, onRowChanged)
                } else {
                    // HERE validation outcome, shown from the very first display of the review table.
                    val verified = row.geocodeStatus == GeocodeStatus.Geocoded
                    Div(attrs = { style { property("font-size", "11px"); property("font-weight", "700"); property("color", if (verified) WebSuccessInk else WebWarning); property("margin-top", "2px") } }) {
                        Text(if (verified) LocalStrings.current.common.addressVerifiedBadge else LocalStrings.current.common.addressNeedsReviewBadge)
                    }
                    if (!row.isLocated()) {
                        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "4px"); property("font-size", "11px"); property("font-weight", "700"); property("color", WebErrorInk); property("margin-top", "2px") } }) {
                            WebIcon(WebIconName.WarningAmber, size = 13, color = WebErrorInk)
                            Text(LocalStrings.current.route.importReviewMissingGpsWarning)
                        }
                        RetryGeocodeLink(row, onRowChanged)
                    }
                }
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextPrimary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.phoneNumber?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextPrimary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.deadlineTime?.takeIf { it.isNotBlank() } ?: strings.dashPlaceholder)
            }
            Td(attrs = { style { padding(12.px, 16.px); property("color", WebTextPrimary); property("font-variant-numeric", "tabular-nums"); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
                Text(row.packageCount.coerceAtLeast(1).toString())
            }
        }

        Td(attrs = { style { padding(12.px, 16.px); property("border-bottom", "1px solid $WebSurfaceDim"); property("white-space", "nowrap") } }) {
            Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("justify-content", "flex-end") } }) {
                RowActionButton(WebIconName.Edit, LocalStrings.current.routeCreation.editDeliveryTitle, onEdit)
                RowActionButton(WebIconName.Close, LocalStrings.current.routeCreation.deleteDelivery, onRemove)
            }
        }
    }
}

@Composable
private fun RowActionButton(icon: WebIconName, label: String, onClick: () -> Unit) {
    Button(
        attrs = {
            attr("aria-label", label)
            attr("title", label)
            onClick { onClick() }
            style {
                width(30.px); height(30.px); property("border-radius", "9px")
                property("border", "none"); property("background", WebSurfaceDim); property("color", WebTextSecondary)
                property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
                property("cursor", "pointer"); padding(0.px)
            }
        },
    ) { WebIcon(icon, size = 16) }
}

@Composable
private fun FilterChip(text: String, selected: Boolean, danger: Boolean, onClick: () -> Unit) {
    val (background, ink) = when {
        danger && selected -> WebError to "#FFFFFF"
        danger -> WebErrorBg to WebErrorInk
        selected -> WebTextPrimary to "#FFFFFF"
        else -> WebSurfaceDim to WebTextPrimary
    }
    Button(
        attrs = {
            onClick { onClick() }
            style {
                property("border", "none"); property("border-radius", "999px"); padding(6.px, 11.px)
                property("font-size", "12px"); property("font-weight", "700"); property("cursor", "pointer")
                property("background", background); property("color", ink)
            }
        },
    ) { Text(text) }
}

// Même convention que DeliveryEntryViewModel.fetchAddressSuggestions (mobile) — seuil et délai de
// debounce partagés pour que web et mobile se comportent identiquement contre le même budget HERE.
private const val ADDRESS_AUTOCOMPLETE_MIN_QUERY_LENGTH = 3
private const val ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS = 400L

/** HERE autocomplete input — the edit / manual-entry modal ([RowEditorDialog]) and MIL-99's
 * "Nouvelle adresse" field in [LoadingAddressCard]. */
@Composable
internal fun AddressAutocompleteInput(
    value: String,
    onChange: (String) -> Unit,
    onSuggestionPicked: (AddressSuggestion) -> Unit,
    placeholderText: String? = null,
    enabled: Boolean = true,
    /** Retour 26/10 (R4) — the address was refused: red border. */
    invalid: Boolean = false,
) {
    val deliveryRepository: DeliveryRepository = koinInject()
    val scope = rememberCoroutineScope()
    var suggestions by remember { mutableStateOf<List<AddressSuggestion>>(emptyList()) }
    var isSearching by remember { mutableStateOf(false) }
    var isDropdownOpen by remember { mutableStateOf(false) }
    var autocompleteJob by remember { mutableStateOf<Job?>(null) }

    fun fetchSuggestions(query: String) {
        autocompleteJob?.cancel()
        val trimmed = query.trim()
        if (trimmed.length < ADDRESS_AUTOCOMPLETE_MIN_QUERY_LENGTH) {
            suggestions = emptyList(); isSearching = false
            return
        }
        isSearching = true
        autocompleteJob = scope.launch {
            delay(ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS)
            try {
                suggestions = deliveryRepository.autocompleteAddress(trimmed)
                isSearching = false
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                suggestions = emptyList(); isSearching = false
            }
        }
    }

    Div(attrs = { style { position(Position.Relative) } }) {
            Input(
                type = InputType.Text,
                attrs = {
                    value(value)
                    if (placeholderText != null) placeholder(placeholderText)
                    if (!enabled) disabled()
                    onInput { event -> onChange(event.value); isDropdownOpen = true; fetchSuggestions(event.value) }
                    addEventListener("focus") { if (value.trim().length >= ADDRESS_AUTOCOMPLETE_MIN_QUERY_LENGTH) isDropdownOpen = true }
                    // onMouseDown (pas onClick) sur chaque suggestion ci-dessous préempte ce blur —
                    // sans ça le dropdown se ferme avant que le clic sur une suggestion ne s'enregistre.
                    addEventListener("blur") { isDropdownOpen = false }
                    style {
                        width(100.percent); property("box-sizing", "border-box")
                        property("border", if (invalid) "2px solid $WebError" else "1px solid $WebBorder"); property("border-radius", "6px")
                        property("padding", "6px 8px"); property("font-size", "13px")
                    }
                },
            )
            if (isDropdownOpen && (isSearching || suggestions.isNotEmpty())) {
                Div(
                    attrs = {
                        style {
                            position(Position.Absolute); property("top", "100%"); property("left", "0"); property("right", "0")
                            property("z-index", "10"); property("margin-top", "2px")
                            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("box-shadow", "0 8px 20px rgba(30,58,74,0.18)")
                            property("max-height", "220px"); property("overflow-y", "auto")
                        }
                    },
                ) {
                    if (isSearching && suggestions.isEmpty()) {
                        Div(attrs = { style { padding(10.px, 14.px); property("font-size", "12.5px"); property("color", WebTextSecondary) } }) {
                            Text(LocalStrings.current.common.loading)
                        }
                    } else {
                        suggestions.forEach { suggestion ->
                            Div(
                                attrs = {
                                    addEventListener("mousedown") { event ->
                                        event.preventDefault()
                                        onSuggestionPicked(suggestion)
                                        suggestions = emptyList(); isDropdownOpen = false
                                    }
                                    style {
                                        padding(10.px, 14.px); property("font-size", "13px"); property("color", WebTextPrimary)
                                        property("cursor", "pointer")
                                    }
                                },
                            ) { Text(suggestion.label) }
                        }
                    }
                }
            }
    }
}

/** MIL-87 (révisé) — un `<input type="time">` natif stocke/émet toujours "HH:mm" en interne, mais
 * sur Windows, Chromium affiche son sélecteur selon le format régional du SYSTÈME D'EXPLOITATION,
 * pas selon l'attribut `lang` de la page (testé : `<html lang="fr">` déjà présent depuis le début,
 * sans effet) — un angle mort impossible à corriger depuis la page elle-même. Deux `WebNativeSelect`
 * HH/mm remplacent l'input natif : le 24h est garanti quel que soit le poste, au prix du widget
 * natif (icône horloge) que l'AC d'origine demandait. Vide (les deux "--") = null, jamais une
 * chaîne vide stockée — même contrat que l'input natif qu'il remplace. */
@Composable
internal fun TimeField(value: String?, onChange: (String?) -> Unit) {
    val (initialHour, initialMinute) = value
        ?.takeIf { it.length == 5 && it[2] == ':' }
        ?.let { it.substring(0, 2) to it.substring(3, 5) }
        ?: (null to null)
    // Held independently from [value] (seeded once, not re-derived every recomposition): picking
    // only the hour must survive on screen even though combineTime(hour, null) is null — deriving
    // currentHour straight from [value] each time erased the just-picked hour the instant the
    // parent recomposed with that null, making the field impossible to fill in two clicks.
    var selectedHour by remember { mutableStateOf(initialHour) }
    var selectedMinute by remember { mutableStateOf(initialMinute) }

    run {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "4px") } }) {
            WebNativeSelect(
                options = listOf<Pair<String?, String>>(null to "--") + (0..23).map { it.toString().padStart(2, '0') }.map { it to it },
                selectedValue = selectedHour,
                minWidth = "68px",
                onSelect = { hour ->
                    selectedHour = hour
                    // Choisir une heure sans minute ne doit pas laisser le champ "non fixé" : "00" par défaut.
                    if (hour != null && selectedMinute == null) selectedMinute = "00"
                    onChange(combineTime(hour, selectedMinute))
                },
            )
            Text(":")
            WebNativeSelect(
                options = listOf<Pair<String?, String>>(null to "--") + (0..59).map { it.toString().padStart(2, '0') }.map { it to it },
                selectedValue = selectedMinute,
                minWidth = "68px",
                onSelect = { minute -> selectedMinute = minute; onChange(combineTime(selectedHour, minute)) },
            )
        }
    }
}

private fun combineTime(hour: String?, minute: String?): String? =
    if (hour != null && minute != null) "$hour:$minute" else null

/** Compose HTML equivalent of mobile's `PackageCountStepper` (+/- row, same floor of 1 enforced here
 * as the ViewModel-side `coerceAtLeast(1)` there) — no native HTML stepper is used, matching the
 * mobile convention rather than a bare `<input type="number">`. */
@Composable
internal fun PackageCountStepper(value: Int, onChange: (Int) -> Unit) {
    run {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "6px") } }) {
            StepperButton("–", enabled = value > 1) { onChange((value - 1).coerceAtLeast(1)) }
            Div(
                attrs = { style { width(24.px); property("text-align", "center"); property("font-variant-numeric", "tabular-nums"); property("font-weight", "600") } },
            ) { Text(value.toString()) }
            StepperButton("+", enabled = true) { onChange(value + 1) }
        }
    }
}

@Composable
private fun StepperButton(symbol: String, enabled: Boolean, onClick: () -> Unit) {
    Button(
        attrs = {
            if (enabled) onClick { onClick() } else attr("disabled", "true")
            style {
                width(22.px); height(22.px); property("border-radius", "6px")
                property("border", "1px solid $WebBorder"); property("background", WebSurface)
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("cursor", if (enabled) "pointer" else "not-allowed"); property("opacity", if (enabled) "1" else "0.4")
                property("font-size", "14px"); property("line-height", "1"); property("padding", "0")
            }
        },
    ) { Text(symbol) }
}

internal enum class InlineBannerKind { Info, Warning, Error, Success }

@Composable
internal fun InlineBanner(kind: InlineBannerKind, text: String) {
    val (bg, ink) = when (kind) {
        InlineBannerKind.Info -> WebActiveTint to WebPrimary
        InlineBannerKind.Warning -> WebWarningBg to WebWarning
        InlineBannerKind.Error -> WebErrorBg to WebErrorInk
        InlineBannerKind.Success -> WebSuccessBg to WebSuccessInk
    }
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                property("border-radius", "10px"); padding(11.px, 14.px)
                property("font-size", "13px"); property("margin-bottom", "14px")
                property("background", bg); property("color", ink)
            }
        },
    ) { Text(text) }
}
